import { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Divider,
  Stack,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  alpha,
  styled,
} from '@mui/material';

import { Close, PersonOffOutlined, PersonAddOutlined, EmailOutlined, CalendarTodayOutlined } from '@mui/icons-material';
import { type UserVM, UserRole, UserStatus } from '@tto/shared-models';

import { useUpdateUserRoleMutation, useGetUserAuditLogsQuery } from '../../../services/restApi/queries/users.service';
import { toast } from 'react-toastify';

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DrawerContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  height: 'calc(100% - 140px)',
}));

const DrawerActions = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  gap: theme.spacing(2),
  flexDirection: 'column',
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

// Currently not in use, but keeping for future role-based UI adjustments
const StatusChip = styled(Chip)<{ status: UserStatus }>(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case UserStatus.ACTIVE:
        return {
          bg: alpha(theme.palette.success.main, 0.1),
          color: theme.palette.success.dark,
        };
      case UserStatus.INVITED:
        return {
          bg: alpha(theme.palette.info.main, 0.1),
          color: theme.palette.info.dark,
        };
      case UserStatus.DEACTIVATED:
        return {
          bg: alpha(theme.palette.error.main, 0.1),
          color: theme.palette.error.dark,
        };
      default:
        return {
          bg: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.grey[700],
        };
    }
  };

  const colors = getStatusColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
  };
});

const RoleChip = styled(Chip)<{ role: UserRole }>(({ theme, role }) => {
  const getRoleColor = () => {
    switch (role) {
      case UserRole.ADMIN:
        return {
          bg: alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.dark,
        };
      case UserRole.USER:
        return {
          bg: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.grey[700],
        };
      default:
        return {
          bg: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.grey[700],
        };
    }
  };

  const colors = getRoleColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontWeight: 600,
  };
});

const AuditLogItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: alpha(theme.palette.grey[500], 0.05),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
}));

interface UserDetailDrawerProps {
  user: UserVM | null;
  open: boolean;
  onClose: () => void;
  currentUserRole: string;
  onDeactivate?: (user: UserVM) => void;
  onReinstate?: (user: UserVM) => void;
}

export const UserDetailDrawer = ({
  user,
  open,
  onClose,
  currentUserRole,
  onDeactivate,
  onReinstate,
}: UserDetailDrawerProps) => {
  const [selectedRole, setSelectedRole] = useState<UserRole | ''>('');
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);

  const [updateUserRole] = useUpdateUserRoleMutation();

  // Fetch audit logs for this user
  const { data: auditLogs, isLoading: auditLogsLoading } = useGetUserAuditLogsQuery(user?.id || '', {
    skip: !user?.id,
  });

  // Reset selected role when user changes
  useState(() => {
    if (user) {
      setSelectedRole(user.role as UserRole);
    }
  });

  if (!user) return null;

  const fullName = `${user.firstName} ${user.lastName}`;
  const initials = `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Not currently available on the UserSchema - keeping for future implementation
  /*
  const handleRoleChange = async () => {
    if (!selectedRole || selectedRole === user.role) return;

    setIsUpdatingRole(true);
    try {
      await updateUserRole({
        userId: user.id,
        input: { newRole: selectedRole },
      }).unwrap();
      toast.success(`Role updated to ${selectedRole.replace('_', ' ')}`);
    } catch (error: any) {
      const errorMessage = error?.data || error?.message || 'Failed to update role';
      toast.error(errorMessage);
      setSelectedRole(user.role as UserRole); // Reset on error
    } finally {
      setIsUpdatingRole(false);
    }
  };

  // Determine available actions based on permissions
  // All Admins have full access to all user management functions
  const canChangeRole = currentUserRole === UserRole.ADMIN;
  const canDeactivate = (user.status === UserStatus.ACTIVE && currentUserRole === UserRole.ADMIN) || true;
  // Note: Should also check user.id !== currentUserId to prevent self-deactivation
  const canReinstate = user.status === UserStatus.DEACTIVATED && currentUserRole === UserRole.ADMIN;
  const canDelete = currentUserRole === UserRole.ADMIN;
*/
  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 500 } },
      }}
    >
      {/* Header */}
      <DrawerHeader>
        <Typography variant='h6' fontWeight={600}>
          User Details
        </Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DrawerHeader>

      {/* Content */}
      <DrawerContent>
        {/* User Profile */}
        <Stack alignItems='center' spacing={2} mb={3}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              fontSize: '2rem',
              bgcolor: 'primary.main',
            }}
          >
            {initials}
          </Avatar>
          <Box textAlign='center'>
            <Typography variant='h5' fontWeight={600} mb={0.5}>
              {fullName}
            </Typography>
            <Stack direction='row' spacing={1} justifyContent='center' mb={1}>
              <RoleChip role={user.role as UserRole} label={user.role.replace('_', ' ')} size='small' />
              {/* <StatusChip status={user.status as UserStatus} label={user.status ?? UserStatus.ACTIVE} size='small' /> */}
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* User Information */}
        <Typography variant='subtitle2' fontWeight={600} mb={2}>
          Information
        </Typography>

        <InfoRow>
          <EmailOutlined color='action' />
          <Box flex={1}>
            <Typography variant='caption' color='text.secondary'>
              Email
            </Typography>
            <Typography variant='body2'>{user.email}</Typography>
          </Box>
        </InfoRow>

        {/* <InfoRow>
          <CalendarTodayOutlined color='action' />
          <Box flex={1}>
            <Typography variant='caption' color='text.secondary'>
              Created
            </Typography>
            <Typography variant='body2'>{formatDate(user.createdAt)}</Typography>
          </Box>
        </InfoRow>

        {user.status === UserStatus.DEACTIVATED && (
          <Alert severity='warning' sx={{ mt: 2 }}>
            This user was deactivated on {formatDate(user.deactivatedAt)}
          </Alert>
        )}

        {user.status === UserStatus.INVITED && (
          <Alert severity='info' sx={{ mt: 2 }}>
            This user has been invited but hasn't accepted the invitation yet.
          </Alert>
        )} */}

        <Divider sx={{ my: 3 }} />

        {/* Role Management */}
        {/* {canChangeRole && (
          <>
            <Typography variant='subtitle2' fontWeight={600} mb={2}>
              Role Management
            </Typography>

            <FormControl fullWidth size='small' sx={{ mb: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value as UserRole)}
                label='Role'
                disabled={isUpdatingRole}
              >
                <MenuItem value={UserRole.USER}>User</MenuItem>
                <MenuItem value={UserRole.ADMIN}>Admin</MenuItem>
              </Select>
            </FormControl>

            {selectedRole !== user.role && (
              <Button
                variant='contained'
                fullWidth
                onClick={handleRoleChange}
                disabled={isUpdatingRole}
                startIcon={isUpdatingRole && <CircularProgress size={16} />}
              >
                {isUpdatingRole ? 'Updating...' : 'Update Role'}
              </Button>
            )}

            <Divider sx={{ my: 3 }} />
          </>
        )} */}

        {/* Activity Timeline */}
        <Typography variant='subtitle2' fontWeight={600} mb={2}>
          Activity Timeline
        </Typography>

        {auditLogsLoading && (
          <Box display='flex' justifyContent='center' py={2}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!auditLogsLoading && auditLogs && auditLogs.length === 0 && (
          <Typography variant='body2' color='text.secondary'>
            No activity recorded yet
          </Typography>
        )}

        {!auditLogsLoading && auditLogs && auditLogs.length > 0 && (
          <Stack spacing={1}>
            {auditLogs.slice(0, 10).map((log) => (
              <AuditLogItem key={log.id}>
                <Typography variant='body2' fontWeight={600}>
                  {log.action.replace(/_/g, ' ')}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {formatDate(log.createdAt)}
                </Typography>
              </AuditLogItem>
            ))}
          </Stack>
        )}
      </DrawerContent>

      {/* Actions */}
      {/* <DrawerActions>
        {canReinstate && (
          <Button
            variant='contained'
            color='success'
            startIcon={<PersonAddOutlined />}
            onClick={() => onReinstate?.(user)}
            fullWidth
          >
            Reinstate User
          </Button>
        )}
        {canDeactivate && (
          <Button
            variant='outlined'
            color='warning'
            startIcon={<PersonOffOutlined />}
            onClick={() => onDeactivate?.(user)}
            fullWidth
          >
            Deactivate User
          </Button>
        )}
      </DrawerActions> */}
    </Drawer>
  );
};
export function formatDistanceToNow(date: Date, options: { addSuffix: boolean }): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const intervals: { [key: string]: number } = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
  };

  for (const [key, secondsInInterval] of Object.entries(intervals)) {
    const interval = Math.floor(diffInSeconds / secondsInInterval);
    if (interval >= 1) {
      const suffix = options.addSuffix ? ' ago' : '';
      return `${interval} ${key}${interval > 1 ? 's' : ''}${suffix}`;
    }
  }

  return options.addSuffix ? 'just now' : '0 seconds';
}
